import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer, Brush, Tooltip, ReferenceLine,
} from 'recharts';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { useTranslation } from 'react-i18next';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

// Config base per i canali noti — il backend può sovrascrivere/aggiungere
const CHANNEL_CONFIG_BASE = {
  Speed_kmh:    { label: 'Speed',     unit: 'km/h', default: true,  color: '#4FC3F7', yDomain: [0, 'auto'] },
  Throttle_pct: { label: 'Throttle',  unit: '%',    default: true,  color: '#81C784', yDomain: [0, 100] },
  LonAcc_g:     { label: 'Brake/Acc', unit: 'g',    default: true,  color: '#EF9A9A', yDomain: ['auto', 'auto'] },
  LatAcc_g:     { label: 'Lat G',     unit: 'g',    default: false, color: '#CE93D8', yDomain: ['auto', 'auto'] },
  RPM:          { label: 'RPM',       unit: 'rpm',  default: false, color: '#FFB74D', yDomain: [0, 'auto'] },
  Gear:         { label: 'Gear',      unit: '',     default: false, color: '#80CBC4', yDomain: [0, 8] },
};

// Risolve config per un canale: usa base locale se disponibile, poi metadati backend
function getChCfg(key, backendMeta) {
  const local   = CHANNEL_CONFIG_BASE[key] || {};
  const backend = backendMeta || {};
  return {
    label:   local.label   ?? backend.label   ?? key,
    unit:    local.unit    ?? backend.unit    ?? '',
    color:   local.color   ?? backend.color   ?? '#90A4AE',
    yDomain: local.yDomain ?? backend.yDomain ?? ['auto', 'auto'],
    default: local.default ?? backend.default ?? false,
  };
}

const LAP_COLORS = ['#FF9800', '#F06292', '#AED581', '#4DD0E1', '#FFD54F', '#A5D6A7', '#9FA8DA'];

// ── Hook: trova il punto dati più vicino a una posizione X del mouse ──────────
function useNearestPoint(data) {
  return useCallback((chartX, containerWidth, totalDist) => {
    if (!data.length || !containerWidth) return null;
    const ratio = chartX / containerWidth;
    const idx = Math.round(ratio * (data.length - 1));
    return data[Math.min(Math.max(idx, 0), data.length - 1)] || null;
  }, [data]);
}

export default function TelemetryCharts({ telemetry, sectors, onHoverPoint, onPinPoint, activeLapNums: externalActiveLapNums, onActiveLapNumsChange }) {
  const { t } = useTranslation('race_engineer');
  const { series = [], channels = [], other_laps = [] } = telemetry || {};
  const lastHoverRef = useRef(null); // salva l'ultimo punto hovrato con Lat/Lon validi

  // Mappa key→metadati backend per lookup rapido
  const chMetaMap = useMemo(() => Object.fromEntries(channels.map(c => [c.key, c])), [channels]);

  const initialChannels = useMemo(
    () => new Set(channels.filter(c => c.default).map(c => c.key)),
    [channels]
  );
  const [activeChannels, setActiveChannels] = useState(() => new Set(initialChannels));
  // activeLapNums è controllato dal parent se viene passato externalActiveLapNums
  const [internalActiveLapNums, setInternalActiveLapNums] = useState(new Set());
  const activeLapNums    = externalActiveLapNums ?? internalActiveLapNums;
  const setActiveLapNums = onActiveLapNumsChange ?? setInternalActiveLapNums;
  const [showRef, setShowRef]               = useState(true);
  const [pinnedLocal, setPinnedLocal]       = useState(null);

  const [showBest, setShowBest]     = useState(true);
  // Zoom: domain [minD, maxD] o null = tutto
  const [zoomDomain, setZoomDomain] = useState(null);

  // Distanze (m) dei confini di settore — calcolate una sola volta dalla serie
  const sectorDistances = useMemo(() => {
    if (!sectors?.length || !series.length) return [];
    return sectors.map(s => {
      if (s.t_start == null) return null;
      // Cerca il punto con Lap_Time più vicino a t_start
      let best = null, bestDiff = Infinity;
      for (const pt of series) {
        if (pt.Lap_Time == null) continue;
        const diff = Math.abs(pt.Lap_Time - s.t_start);
        if (diff < bestDiff) { bestDiff = diff; best = pt.d; }
      }
      return best;
    }).filter(d => d != null);
  }, [sectors, series]);

  useEffect(() => {
    setZoomDomain(null);
    setPinnedLocal(null);
  }, [series.length]);

  const toggleChannel = useCallback((key) => {
    setActiveChannels(prev => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key); else s.add(key);
      return s;
    });
  }, []);

  const toggleLap = useCallback((n) => {
    setActiveLapNums(prev => {
      const s = new Set(prev);
      if (s.has(n)) s.delete(n); else s.add(n);
      return s;
    });
  }, []);

  const hasRef = useMemo(
    () => series.some(pt => pt.r_Speed_kmh != null || pt.r_Throttle_pct != null),
    [series]
  );

  const mergedData = useMemo(() => {
    if (!series.length) return [];
    const selected = other_laps.filter(l => activeLapNums.has(l.lap_number));
    if (!selected.length) return series;
    return series.map((pt, i) => {
      const merged = { ...pt };
      selected.forEach(lap => {
        const op = lap.telemetry[i];
        if (!op) return;
        for (const [k, v] of Object.entries(op)) {
          if (k !== 'd') merged[`l${lap.lap_number}_${k}`] = v;
        }
      });
      return merged;
    });
  }, [series, other_laps, activeLapNums]);

  // Dati filtrati per zoom (per rendering efficiente)
  const displayData = useMemo(() => {
    if (!zoomDomain) return mergedData;
    return mergedData.filter(pt => pt.d >= zoomDomain[0] && pt.d <= zoomDomain[1]);
  }, [mergedData, zoomDomain]);

  // Hover: aggiorna pallino sulla mappa E salva posizione in ref per il click.
  // Doppia strategia: usa activePayload se disponibile, altrimenti cerca per distanza.
  const handleMouseMove = useCallback((data) => {
    // Strategia 1: activePayload (standard)
    const pt = data?.activePayload?.[0]?.payload;
    if (pt?.Lat != null && pt?.Lon != null) {
      lastHoverRef.current = { lat: pt.Lat, lon: pt.Lon, d: pt.d };
      onHoverPoint?.(pt.Lat, pt.Lon);
      return;
    }
    // Strategia 2: cerca il punto più vicino nella serie per distanza
    const activeD = data?.activeLabel != null ? Number(data.activeLabel) : null;
    if (activeD == null || !displayData.length) return;
    let nearest = null, nearestDiff = Infinity;
    for (const p of displayData) {
      if (p.Lat == null || p.Lon == null) continue;
      const diff = Math.abs((p.d ?? 0) - activeD);
      if (diff < nearestDiff) { nearestDiff = diff; nearest = p; }
    }
    if (nearest) {
      lastHoverRef.current = { lat: nearest.Lat, lon: nearest.Lon, d: nearest.d };
      onHoverPoint?.(nearest.Lat, nearest.Lon);
    }
  }, [onHoverPoint, displayData]);

  const handleMouseLeave = useCallback(() => {
    onHoverPoint?.(null, null);
  }, [onHoverPoint]);

  // Click: legge l'ultima posizione nota dal ref (non dipende da activePayload)
  const handleClick = useCallback(() => {
    if (!lastHoverRef.current) return;
    const { lat, lon, d } = lastHoverRef.current;
    const isSame = pinnedLocal && Math.abs(pinnedLocal.d - d) < 5;
    const pinned = isSame ? null : { lat, lon, d };
    setPinnedLocal(pinned);
    onPinPoint?.(pinned?.lat ?? null, pinned?.lon ?? null);
  }, [pinnedLocal, onPinPoint]);

  // Brush overview → aggiorna zoomDomain
  const handleBrushChange = useCallback((range) => {
    if (range?.startIndex == null) return;
    const minD = mergedData[range.startIndex]?.d;
    const maxD = mergedData[range.endIndex]?.d;
    if (minD != null && maxD != null && maxD > minD) {
      setZoomDomain([minD, maxD]);
    }
  }, [mergedData]);

  const handleResetZoom = useCallback(() => {
    setZoomDomain(null);
  }, []);

  if (!series.length) return null;

  const activeList = [...activeChannels].filter(k => channels.some(c => c.key === k));
  const selectedOther = other_laps.filter(l => activeLapNums.has(l.lap_number));
  const isZoomed = !!zoomDomain;
  const fullMinD = mergedData[0]?.d ?? 0;
  const fullMaxD = mergedData[mergedData.length - 1]?.d ?? 1;

  // Props comuni XAxis
  const xAxisProps = (isLast) => ({
    dataKey: 'd',
    type: 'number',
    domain: zoomDomain || [fullMinD, fullMaxD],
    allowDataOverflow: true,
    tick: isLast ? { fontSize: 9, fill: 'rgba(255,255,255,0.3)' } : false,
    axisLine: isLast ? { stroke: 'rgba(255,255,255,0.1)' } : false,
    tickLine: false,
    height: isLast ? 14 : 2,
    tickFormatter: v => `${Math.round(v)}m`,
  });

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>

      {/* ── Header ──────────────────────────────────────────────── */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1 }}>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
          <ShowChartIcon sx={{ fontSize: 17, color: 'secondary.main', mr: 0.5 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ mr: 0.5 }}>{t('telemetry.title')}</Typography>

          {isZoomed && (
            <Chip
              icon={<ZoomOutIcon sx={{ fontSize: '14px !important' }} />}
              label={t('telemetry.reset_zoom')}
              size="small"
              onClick={handleResetZoom}
              sx={{ fontSize: 10, height: 20, cursor: 'pointer', bgcolor: 'rgba(255,152,0,0.15)', borderColor: '#FF9800', border: '1px solid', color: '#FF9800' }}
            />
          )}

          {pinnedLocal && (
            <Chip
              icon={<GpsFixedIcon sx={{ fontSize: '12px !important' }} />}
              label={`${Math.round(pinnedLocal.d)}m`}
              size="small"
              onClick={() => { setPinnedLocal(null); onPinPoint?.(null, null); }}
              sx={{ fontSize: 10, height: 20, cursor: 'pointer', bgcolor: 'rgba(255,235,59,0.15)', borderColor: '#FFEB3B', border: '1px solid', color: '#FFEB3B' }}
            />
          )}

          <Box sx={{ flex: 1 }} />

          {channels.map(ch => {
            const cfg = getChCfg(ch.key, ch);
            const on = activeChannels.has(ch.key);
            return (
              <Chip key={ch.key} label={cfg.label} size="small" onClick={() => toggleChannel(ch.key)}
                sx={{
                  fontSize: 10, height: 20, cursor: 'pointer',
                  bgcolor: on ? `${cfg.color}22` : 'transparent',
                  borderColor: on ? cfg.color : 'action.disabled',
                  color: on ? cfg.color : 'text.disabled',
                  border: '1px solid',
                }}
              />
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>{t('telemetry.laps_label')}</Typography>
          <Chip label="Best" size="small" onClick={() => setShowBest(v => !v)}
            sx={{ fontSize: 10, height: 20, cursor: 'pointer', border: '1px solid',
              bgcolor:     showBest ? 'rgba(63,81,181,0.2)' : 'transparent',
              borderColor: showBest ? 'secondary.main' : 'action.disabled',
              color:       showBest ? 'secondary.main'  : 'text.disabled',
            }}
          />
          {hasRef && (
            <Chip label="Rif." size="small" onClick={() => setShowRef(v => !v)}
              sx={{
                fontSize: 10, height: 20, cursor: 'pointer',
                bgcolor: showRef ? 'rgba(180,180,180,0.12)' : 'transparent',
                borderColor: showRef ? 'rgba(200,200,200,0.5)' : 'action.disabled',
                color: showRef ? 'text.secondary' : 'text.disabled',
                border: '1px solid',
              }}
            />
          )}
          {other_laps.map((lap, i) => {
            const on = activeLapNums.has(lap.lap_number);
            const color = LAP_COLORS[i % LAP_COLORS.length];
            return (
              <Chip key={lap.lap_number}
                label={`G${lap.lap_number} · ${lap.lap_time_str}`}
                size="small" onClick={() => toggleLap(lap.lap_number)}
                sx={{
                  fontSize: 10, height: 20, cursor: 'pointer',
                  bgcolor: on ? `${color}22` : 'transparent',
                  borderColor: on ? color : 'action.disabled',
                  color: on ? color : 'text.disabled',
                  border: '1px solid',
                }}
              />
            );
          })}
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, ml: 1 }}>
            {t('telemetry.hint')}
          </Typography>
        </Box>
      </Box>

      {/* ── Grafici canali ──────────────────────────────────────── */}
      <Box sx={{ pt: 0.5, pb: 0 }}>
        {activeList.map((channelKey, idx) => {
          const cfg = getChCfg(channelKey, chMetaMap[channelKey]);
          const isLast = idx === activeList.length - 1;

          return (
            <Box key={channelKey} sx={{ px: 1 }}>
              <Typography sx={{ fontSize: 9, color: cfg.color, fontWeight: 600, lineHeight: 1.2, pl: '34px', display: 'block' }}>
                {cfg.label}{cfg.unit ? ` (${cfg.unit})` : ''}
              </Typography>
              <ResponsiveContainer width="100%" height={isLast ? 70 : 60}>
                <LineChart
                  data={displayData}
                  margin={{ top: 2, right: 6, left: 32, bottom: isLast ? 8 : 0 }}
                  syncId="telem-sync"
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  onClick={handleClick}
                  style={{ cursor: 'pointer' }}
                >
                  <CartesianGrid strokeDasharray="2 6" stroke="rgba(255,255,255,0.04)" vertical={false} />
                  <XAxis {...xAxisProps(isLast)} />
                  <YAxis
                    domain={cfg.yDomain}
                    tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.3)' }}
                    tickLine={false} axisLine={false} width={30} tickCount={3}
                  />

                  {/* Tooltip invisibile — necessario per attivare activePayload su click */}
                  <Tooltip content={() => null} cursor={{ stroke: 'rgba(255,255,255,0.2)', strokeWidth: 1 }} />

                  {/* Linee verticali confini settore */}
                  {sectorDistances.map((d, i) => (
                    <ReferenceLine key={i} x={d} stroke="rgba(255,255,255,0.12)" strokeDasharray="3 3"
                      label={isLast ? { value: `S${i + 2}`, position: 'top', fontSize: 8, fill: 'rgba(255,255,255,0.3)' } : undefined}
                    />
                  ))}

                  {/* Main lap */}
                  {showBest && (
                    <Line type="monotone" dataKey={channelKey}
                      stroke={cfg.color} strokeWidth={1.5}
                      dot={false} isAnimationActive={false} connectNulls />
                  )}

                  {/* Reference CSV */}
                  {hasRef && showRef && (
                    <Line type="monotone" dataKey={`r_${channelKey}`}
                      stroke={cfg.color} strokeWidth={1} strokeOpacity={0.45}
                      strokeDasharray="6 3" dot={false} isAnimationActive={false} connectNulls />
                  )}

                  {/* Giri selezionati */}
                  {selectedOther.map(lap => (
                    <Line key={lap.lap_number}
                      type="monotone" dataKey={`l${lap.lap_number}_${channelKey}`}
                      stroke={LAP_COLORS[other_laps.indexOf(lap) % LAP_COLORS.length]}
                      strokeWidth={1.2} strokeOpacity={0.85}
                      dot={false} isAnimationActive={false} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          );
        })}

        {/* ── Overview / Zoom brush ────────────────────────────── */}
        <Box sx={{ px: 1, pb: 1 }}>
          <Typography sx={{ fontSize: 9, color: 'text.disabled', pl: '34px', lineHeight: 1.5 }}>
            Zoom
          </Typography>
          <ResponsiveContainer width="100%" height={38}>
            <LineChart
              data={mergedData}
              margin={{ top: 0, right: 6, left: 32, bottom: 0 }}
            >
              <XAxis dataKey="d" type="number" domain={[fullMinD, fullMaxD]} hide />
              <YAxis hide domain={['auto', 'auto']} />
              <Line type="monotone" dataKey="Speed_kmh"
                stroke="rgba(79,195,247,0.35)" strokeWidth={1}
                dot={false} isAnimationActive={false} connectNulls />
              <Brush
                dataKey="d"
                height={18}
                stroke="rgba(255,255,255,0.15)"
                fill="rgba(255,255,255,0.03)"
                travellerWidth={8}
                tickFormatter={() => ''}
                onChange={handleBrushChange}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Paper>
  );
}
